/**
 * DedupFlagBanner — Phase R10.4 (2026-07-05)
 *
 * Top-of-detail-page banner that surfaces the current claim's
 * `dedup_flag` + `dedup_flag_reason`. Hidden on green / null flags.
 *
 * Variants:
 *   - yellow  → amber   banner, "Needs Verification: {reason}"
 *   - orange  → orange  banner, "Potential Duplicate: {reason}" + "View Comparison" link
 *   - red     → red     banner, "Confirmed Duplicate: {reason}" + "View Comparison" link
 *
 * Sticky-dismissible (visual compaction only, no persistence) — on reload
 * the banner reappears automatically.
 *
 * Props:
 *   flag                       — "green" | "yellow" | "orange" | "red" | null
 *   reason                     — short string
 *   matchingClaimCount         — int (if 0, hide "View Comparison" link)
 *   onScrollToComparison()     — scroll to <DuplicateComparisonPanel />
 */
import { useState } from 'react';
import { AlertTriangle, AlertOctagon, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';

const VARIANTS = {
  yellow: {
    label:   'Needs Verification',
    cls:     'border-amber-500/40 bg-amber-500/10',
    textCls: 'text-amber-100',
    accent:  'text-amber-300',
    Icon:    AlertTriangle,
  },
  orange: {
    label:   'Potential Duplicate',
    cls:     'border-orange-500/40 bg-orange-500/10',
    textCls: 'text-orange-100',
    accent:  'text-orange-300',
    Icon:    AlertTriangle,
  },
  red: {
    label:   'Confirmed Duplicate',
    cls:     'border-red-500/40 bg-red-500/10',
    textCls: 'text-red-100',
    accent:  'text-red-300',
    Icon:    AlertOctagon,
  },
};

export default function DedupFlagBanner({
  flag,
  reason,
  matchingClaimCount = 0,
  onScrollToComparison,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const key = (flag || 'green').toLowerCase();
  if (key === 'green') return null;
  const variant = VARIANTS[key];
  if (!variant) return null;

  const canScroll = typeof onScrollToComparison === 'function' && matchingClaimCount > 0;
  const showScrollLink = canScroll && (key === 'orange' || key === 'red');
  const { Icon } = variant;

  return (
    <div
      className={`flex items-start gap-3 rounded-2xl border px-4 py-3 ${variant.cls}`}
      data-testid={`dedup-flag-banner-${key}`}
      role="alert"
    >
      <Icon size={18} className={`${variant.accent} mt-0.5 shrink-0`} />
      <div className={`flex-1 text-sm leading-relaxed ${variant.textCls}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold">
            <span className={variant.accent}>{variant.label}:</span>{' '}
          </span>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="shrink-0 p-1 rounded-md text-white/60 hover:text-white hover:bg-white/5"
            data-testid={`dedup-flag-banner-collapse-${key}`}
            aria-label={collapsed ? 'Expand banner' : 'Collapse banner'}
          >
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
        {!collapsed ? (
          <div className="mt-1 flex flex-col gap-2">
            <span
              className="text-white/85 break-words"
              data-testid={`dedup-flag-banner-reason-${key}`}
            >
              {reason || 'No reason provided.'}
            </span>
            {showScrollLink ? (
              <button
                type="button"
                onClick={onScrollToComparison}
                className={`inline-flex items-center gap-1 self-start text-xs font-semibold uppercase tracking-wider underline-offset-4 hover:underline ${variant.accent}`}
                data-testid={`dedup-flag-banner-view-comparison-${key}`}
              >
                View Comparison <ChevronRight size={12} />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
